import type { Event } from '@/types/event'

/**
 * Shared event-dependency helpers, used by the sync engine's dependency-atomic batching
 * (syncService.createBatch) and by the activity-view derivation (syncActivityService).
 * One implementation so grouping can never disagree with batching.
 */

function addOwnClientId(value: unknown, ids: Set<string>): void {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return
  const record = value as Record<string, unknown>
  const clientId = record.clientId ?? record.client_id
  if (typeof clientId === 'string') {
    ids.add(clientId)
  }
}

function addOwnClientIdsFromArray(value: unknown, ids: Set<string>): void {
  if (!Array.isArray(value)) return
  for (const item of value) {
    addOwnClientId(item, ids)
  }
}

function addSegmentClientIds(value: unknown, ids: Set<string>): void {
  if (!Array.isArray(value)) return
  for (const segment of value) {
    addOwnClientId(segment, ids)
    const points = (segment as Record<string, unknown> | null)?.points
    addOwnClientIdsFromArray(points, ids)
  }
}

function addTrackClientIds(value: unknown, ids: Set<string>): void {
  if (!Array.isArray(value)) return
  for (const track of value) {
    addOwnClientId(track, ids)
    const segments = (track as Record<string, unknown> | null)?.segments
    addSegmentClientIds(segments, ids)
  }
}

/**
 * Entity client-ids that an event *creates*. Most create events introduce one entity under
 * `payload.clientId`; fat `TOUR_CREATED` payloads can also carry nested tracks, segments,
 * waypoints, and route points. Those nested IDs are created by the same event and must count for
 * dependency-atomic batching and quarantine cascades.
 */
export function createdClientIds(event: Event): string[] {
  if (!event.type.endsWith('_CREATED')) return []
  const ids = new Set<string>()
  const payload = event.payload as Record<string, unknown> | undefined
  addOwnClientId(payload, ids)

  if (event.type === 'TOUR_CREATED') {
    addTrackClientIds(payload?.tracks, ids)
    addOwnClientIdsFromArray(payload?.waypoints ?? payload?.way_points, ids)
    addOwnClientIdsFromArray(payload?.routePoints ?? payload?.route_points, ids)
  } else if (event.type === 'TRACK_CREATED') {
    addSegmentClientIds(payload?.segments, ids)
  } else if (event.type === 'SEGMENT_CREATED') {
    addOwnClientIdsFromArray(payload?.points, ids)
  }

  return [...ids]
}

/**
 * The primary entity client-id that an event creates, or undefined if the event creates nothing.
 * Kept for callers that need a single label/key; use `createdClientIds` for dependency logic.
 */
export function createdClientId(event: Event): string | undefined {
  return createdClientIds(event)[0]
}

/**
 * All parent entity client-ids an event *references* (the workspace/tour/track it belongs to).
 * Reference fields are any payload key whose name ends in "clientId" / "client_id" (e.g.
 * `tour_client_id`, `trackClientId`, `workspaceClientId`). The bare `clientId` on a `*_CREATED`
 * event is the entity's own id and is excluded; on other events (e.g. TOUR_ADDED_TO_WORKSPACE)
 * `client_id` is a parent reference and is kept.
 */
export function referencedClientIds(event: Event): string[] {
  const payload = event.payload as Record<string, unknown> | undefined
  if (!payload) return []
  const isCreate = event.type.endsWith('_CREATED')
  const refs: string[] = []
  for (const [key, value] of Object.entries(payload)) {
    if (typeof value !== 'string') continue
    const normalized = key.replace(/_/g, '').toLowerCase()
    if (!normalized.endsWith('clientid')) continue
    // Skip the entity's own id on create events; everywhere else `clientId`/`client_id` is a parent ref.
    if (isCreate && (key === 'clientId' || key === 'client_id')) continue
    refs.push(value)
  }
  return refs
}

/**
 * Event clientIds that must be quarantined together with the seed events: any queued event
 * that (transitively) references an entity created by a quarantined event would fail with
 * "not found" forever if left in the queue (sync-feedback.md §6 dependent cascade).
 * `events` must be in queue order (parents before children); returns seeds + dependents.
 */
export function collectDependentEventIds(
  events: Event[],
  seedEventIds: ReadonlySet<string>,
): Set<string> {
  const targetEventIds = new Set(seedEventIds)
  const quarantinedEntityIds = new Set<string>()

  for (const event of events) {
    if (targetEventIds.has(event.clientId)) {
      for (const created of createdClientIds(event)) {
        quarantinedEntityIds.add(created)
      }
      continue
    }
    if (referencedClientIds(event).some((ref) => quarantinedEntityIds.has(ref))) {
      targetEventIds.add(event.clientId)
      for (const created of createdClientIds(event)) {
        quarantinedEntityIds.add(created)
      }
    }
  }

  return targetEventIds
}
